// 11 §8 / §10.10 — the encounter HUD in the mockup's frame: a top bar (room plate, pulse timer,
// screen tabs, ☰), PARTY on the left, OBJECTIVE / FOES / PLAN on the right, the board viewport in
// the centre (pointer-events: none) with its stage (plan-ends stamp, target preview, leader's
// bark), and the bottom bar (hero + slots, action tiles, Undo / Lock plan with who has locked).
// The encounter is BARLESS (Shell.tsx): the Shell pieces live in this top bar.
// docs/84: on the `spoils` route the same HUD stays up over the finished board: PARTY on the left,
// the Spoils panel over the board (SpoilsPanel), the Spoils bar in place of the hotbar (SpoilsBar)
// and the encounter's own chat; the right column and the planning pieces step aside.
import { useEffect, useRef, useState } from 'react';
import { PulseTimer } from './PulseTimer';
import { RosterStrip } from './RosterStrip';
import { ActionQueue, PlanButtons } from './ActionQueue';
import { Hotbar } from './Hotbar';
import { Coach } from './Coach';
import { FilterBar, FoeList, PlanList, PlaybackControls, RuleStrip, goalLines, intentLine, planEnding, useLeader } from './Lists';
import { AlmanacChip, Banner, Button, LiveRegion, Portrait, Reveal, afterMotion, useTypewriter, type StampTone } from '../components';
import { ShellMenuButton, ShellNav, ShellPlate } from '../screens/Shell';
import { useAuthoring } from '../../core/stores/authoring';
import { useSession } from '../../core/stores/session';
import { useView } from '../../core/stores/view';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { FATE, useEncounterRow, useFoeFate, useInstance, useOpeningStory, useWorldClock } from '../../core/subscriptions/hooks';
import { display, withHeroes } from '../../core/story/genesis';
import { actionFor } from '../../core/sim/actions';
import { strengthOf } from '../../core/sim/weapon';
import { DEFAULT_CELL } from '../../config';
import { formatHour } from '../../game/lighting/daylight';
import { type Snapshot, type Tile } from '../../core/sim/types';
import { ChatDock } from '../screens/ChatDock';
import { NamedFoeEscape, OpeningDirector, OpeningFaceoff } from './Opening';
import { TurnHold } from './TurnHold';
import { FoeDefeated, GETAWAY_LINE, RISEN_LINE } from './NamedFoe';
import { DefeatCard } from './Defeat';
import { SightLayer } from '../components/StoryFx';
import { HaulChip, SecuredStrip, useSecured } from './Field';
import { EdgeCues, ModifierCard, ModifierRows } from './Modifiers';
import { FogRows } from './Fog';
import { swayLine, windFor } from '../../core/sim/modifiers';
import { useCinematic } from '../../core/stores/cinematic';
import { HeroButtons, InventoryButton, InventoryDrawer, LootMoment } from './Bag';
import { useGearPlanUpkeep } from '../inventory/fightGear';
import { BoardMenu, BoardPings } from './BoardPings';
import { PuzzlePanel } from './Puzzle';
import { CrawlBriefing, CrawlCard } from './Crawl';
import { BriefingCard, ExploreCard } from './Explore';
import { humanize } from './shared';
import { startFirstTurn } from '../../core/audio/cues';
import { usePlayback } from '../../core/stores/playback';
import { SpoilsBar, SpoilsPanel } from '../screens/Spoils';
import { covers } from '../../core/sim/body';
import { useHuge, type HugeSay } from '../../core/huge/voices';

/** The plan-hash chip is for parity debugging (`?debug=1`); the specs read it hidden. */
const SHOW_DEBUG = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

export function EncounterHud({ spoils = false }: { spoils?: boolean }) {
  useGearPlanUpkeep();
  // docs/84: the Spoils panel folds away to look over the field, and comes back
  const [spoilsOpen, setSpoilsOpen] = useState(true);
  useEffect(() => { if (spoils) setSpoilsOpen(true); }, [spoils]);
  const announcement = useAuthoring((s) => s.announcement);
  const projected = useAuthoring((s) => s.projected);
  const phase = useSession((s) => s.phase);
  const encounterId = useEncounter((s) => s.encounterId);
  const partyId = useEncounter((s) => s.partyId);
  const worldId = useSession((s) => s.worldId);
  const parity = useEncounter((s) => s.parity);
  const dismissParity = useEncounter((s) => s.dismissParity);
  const lastPlanHash = useEncounter((s) => s.lastPlanHash);
  const snapshot = useEncounter((s) => s.snapshot);
  const enc = useEncounterRow(encounterId);
  const boardRef = useRef<HTMLDivElement>(null);
  const setViewport = useView((s) => s.setViewport);
  const zoom = useView((s) => s.zoom);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const publish = () => { const r = el.getBoundingClientRect(); setViewport({ x: Math.round(r.left), y: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) }); };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => { ro.disconnect(); window.removeEventListener('resize', publish); };
  }, [setViewport]);
  // docs/25: on a secured field every plan "ends" it (the objectives stay met); the strip says what happens instead
  const secured = useSecured();
  const faceoff = useCinematic((s) => s.faceoffPlaying);
  // docs/61 EX3: an exploration is won by going on, not by a blow
  const ending = planEnding(projected, snapshot);
  const ended = ending && !secured && !spoils ? (ending === 'Victory' && snapshot?.explore ? { tone: 'victory' as const, word: snapshot.goals?.goals.some((g) => g.kind === 'Find') ? 'Found' : 'Onward' } : ENDINGS[ending] ?? { tone: 'neutral' as const, word: ending }) : undefined;
  const lock = lastPlanHash ? `${lastPlanHash.client.slice(0, 8)} / ${lastPlanHash.server.slice(0, 8)} ${lastPlanHash.client === lastPlanHash.server ? '✓' : '✗'}` : undefined;

  return (
    <div className={`fk-hud ${ended ? `fk-hud--ending fk-hud--ending-${ended.tone}` : ''} ${spoils ? 'fk-hud--spoils' : ''}`} data-testid="encounter-hud" data-phase={phase} data-encounter-id={encounterId?.toString()} data-board-hash={snapshot?.snapshotHash}>
      {parity.length > 0 && (
        <Banner mono className="fk-hud__parity" data-testid="parity-banner">
          <span>PARITY MISMATCH — {parity.map((p) => `${p.kind} @ turn ${p.pulse}: server ${p.expected.slice(0, 12)}… client ${p.got.slice(0, 12)}…`).join(' · ')}</span>
          <Button small onClick={dismissParity}>Dismiss</Button>
        </Banner>
      )}
      <header className="fk-hud__top fk-hit">
        <RoomPlate name={enc?.name} instanceId={enc?.instanceId ?? undefined} encounterId={encounterId} />
        {!spoils && <PulseTimer />}
        {/* docs/83: what the fight wants now lives in the bar, never over the board */}
        {spoils ? <span className="fk-spacer" /> : <HeadObjective ending={ending} ended={ended} />}
        <ShellNav className="fk-hud__nav" />
        <ShellMenuButton />
      </header>
      {/* docs/83: the View box (filters, zoom, fit) stands down; its keys still work (F, M, V, C, Q/E) */}
      <aside className="fk-hud__left" aria-label="Party"><RosterStrip />{SHOW_DEBUG ? <FilterBar /> : <ZoomReadout />}</aside>
      <div className="fk-hud__board" aria-hidden="true" ref={boardRef} />
      <div className="fk-hud__stage" style={{ ['--fk-board-w' as string]: `${(snapshot?.board.w ?? 0) * DEFAULT_CELL * zoom}px`, ['--fk-board-h' as string]: `${(snapshot?.board.h ?? 0) * DEFAULT_CELL * zoom}px` }}>
        {spoils ? <SpoilsPanel open={spoilsOpen} /> : <>
          <EdgeCues />
          <ModifierCard suppressed={!!ended || secured || faceoff} />
          <BoardPings />
          <TargetPreview />
          {!faceoff && <Coach />}
          <LootMoment />
          <InventoryDrawer />
          <OpeningFaceoff />
          <LeaderBark />
          <HugeBark />
          <DefeatCard />
          {/* docs/138 XO9: the exploration's briefing, before the first turn and on asking */}
          {snapshot?.explore && <BriefingCard />}
        </>}
      </div>
      {!spoils && (
        <aside className="fk-hud__right" aria-label="Foes and plan">
          {/* docs/41 PZ11: what the encounter wants comes first */}
          <RuleStrip />
          {snapshot?.explore ? <ExploreCard /> : <><CrawlCard /><CrawlBriefing /></>}
          <PuzzlePanel />
          <HaulChip />
          <FoeList />
          <PlanList />
          <FieldPanel />
          <div className="fk-hud__foot">
            <PlaybackControls debug={SHOW_DEBUG} />
            {/* the plan and snapshot hashes, for parity checks: shown with ?debug=1 */}
            <section className="fk-hud__debug fk-hit" hidden={!SHOW_DEBUG} data-testid="plan-hash-chip" aria-label="Plan hash (debug)" title="plan hash (debug chip): projected now · at last lock client/server">
              <span>turn {enc?.pulseIndex ?? '…'} · plan {projected?.planHash.slice(0, 8) ?? '…'} · snap {snapshot?.snapshotHash.slice(0, 8) ?? '…'}</span>
              {lastPlanHash && <span className={lastPlanHash.client === lastPlanHash.server ? 'fk-hud__debug-ok' : 'fk-hud__debug-bad'}>lock {lastPlanHash.pulse}: {lock}</span>}
            </section>
          </div>
        </aside>
      )}
      <footer className="fk-hud__bottom fk-hit">
        {spoils ? <SpoilsBar open={spoilsOpen} onToggle={() => setSpoilsOpen((o) => !o)} /> : <>
          <ActionQueue />
          <Hotbar />
          <HeroButtons />
          <InventoryButton />
          <PlanButtons />
        </>}
      </footer>
      {!spoils && <OpeningDirector />}
      {!spoils && <TurnHold />}
      {!spoils && <FirstTurnVoice />}
      {!spoils && <BoardMenu />}
      {/* docs/50: a battle beat's sight (fog rolling in, the ground shaking) over the board */}
      <BeatSights />
      <NamedFoeEscape />
      <FoeDefeated />
      {worldId && <ChatDock worldId={worldId} partyId={partyId} variant="dock" placement="board" />}
      <LiveRegion text={announcement} />
    </div>
  );
}

/** The camera's zoom for the specs (`zoom`), now the View box is gone: not drawn. */
function ZoomReadout() {
  const zoom = useView((s) => s.zoomGoal);
  return <span hidden data-testid="zoom">{Number(zoom.toFixed(1))}×</span>;
}

/** "MAIN QUEST · ROOM 2 / THE SLUICE HOUSE": the World plate (`shell-world`), the adventure and room when there is one, the room's name. */
function RoomPlate({ name, instanceId, encounterId }: { name?: string; instanceId?: bigint; encounterId?: bigint }) {
  const inst = useInstance(instanceId);
  const room = inst && encounterId !== undefined ? inst.encounters.findIndex((e) => e === encounterId) + 1 || inst.encounters.length : undefined;
  const sky = useView((s) => s.sky);
  const clock = useWorldClock(useSession((s) => s.worldId));
  // docs/83: two short lines a side — where (World · quest · room, the room's name) and when (hour
  // and sky, the date) — so nothing is squeezed out of the bar's height
  return (
    <div className="fk-hud__room">
      <div className="fk-hud__room-where">
        <span className="fk-hud__room-eyebrow" title={inst ? `${inst.title}${room ? ` · room ${room}` : ''}` : undefined}><ShellPlate className="fk-hud__world" />{inst && <span>· {inst.title}{room ? ` · room ${room}` : ''}</span>}</span>
        <span className="fk-hud__room-name" title={name ?? 'Encounter'}>{name ?? 'Encounter'}</span>
      </div>
      {(sky || clock) && (
        <div className="fk-hud__room-when">
          {sky && <span className="fk-hud__sky" data-testid="hud-sky" title="Time of day and weather in this room">{SKY_GLYPH[sky.weather] ?? (sky.phase === 'night' || sky.phase === 'dusk' ? '☾' : '☀')} {formatHour(sky.hour)} · {sky.weather}</span>}
          {clock && <AlmanacChip clock={clock} year={false} className="fk-hud__date" />}
        </div>
      )}
    </div>
  );
}

/**
 * docs/83: the objective line in the top bar. The secured field's strip (Ready to finish) comes
 * first, then "this plan ends the fight", else the fight's first aim — all things that stay up
 * while the party plays, so none of them covers the board.
 */
function HeadObjective({ ending, ended }: { ending?: string; ended?: { tone: StampTone; word: string } }) {
  const snapshot = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const story = useOpeningStory(encounterId);
  const leaderName = useLeader().name;
  const secured = useSecured();
  if (secured) return <div className="fk-hud__headline"><SecuredStrip /></div>;
  if (ended) {
    return (
      <div className="fk-hud__headline">
        <Reveal kind="pop" key={ending} className={`fk-hud__ended fk-hud__ended--${ended.tone}`} role="status" data-testid="plan-ends" data-outcome={ending}>
          <span className="fk-hud__ended-word">{ended.word}</span>
          <span className="fk-hud__ended-sub">{ended.word === 'Found' ? 'This plan finds the wonder, if it locks as planned' : 'This plan ends the fight, if it locks as planned'}</span>
        </Reveal>
      </div>
    );
  }
  const aim = snapshot ? goalLines(snapshot, leaderName, story?.battle.brief)[0] ?? (snapshot.objective ? `${humanize(snapshot.objective)}.` : undefined) : undefined;
  return (
    <div className="fk-hud__headline">
      {aim && <span className="fk-hud__aim" data-testid="head-objective" title={aim}><span className="fk-hud__label">Objective</span><span className="fk-hud__aim-line">{aim}</span></span>}
    </div>
  );
}

/**
 * FIELD (docs/26, docs/29): the encounter modifiers in force (sun, wind, heat, cold, dark, season),
 * what this fight's ground and sky do — "snow +½ pace", "sight 3", "missiles −2 range" — and the
 * selected hero's footing. Every line is the sim's (`ModifierNote`, `Environment::conditions_on`,
 * `trait_words`); nothing here is a rule.
 */
function FieldPanel() {
  const field = useView((s) => s.field);
  const fogged = useEncounter((s) => !!s.snapshot?.fog);
  if (!fogged && (!field || (!field.conditions.length && !field.traits.length && !field.modifiers.length && !field.moves?.length))) return null;
  if (!field) return <section className="fk-hud__fieldpanel fk-hit" data-testid="hud-field" aria-label="The field"><h3 className="fk-hud__label">Field</h3><FogRows /></section>;
  return (
    <section className="fk-hud__fieldpanel fk-hit" data-testid="hud-field" aria-label="The field: modifiers, ground, weather and footing">
      <h3 className="fk-hud__label">Field</h3>
      <FogRows />
      <ModifierRows notes={field.modifiers} />
      {field.conditions.length ? (
        <ul className="fk-hud__conds">{field.conditions.map((c) => <li key={c}>{c}</li>)}</ul>
      ) : !field.modifiers.length && <span className="fk-hud__hint">Fair going: nothing slows or blinds you.</span>}
      {field.traits.length > 0 && (
        <p className="fk-hud__footing" data-testid="hud-footing" title={field.traits.join('\n')}>
          <b>{field.hero ?? 'Hero'}</b> {field.traits.map((t) => t.split(':')[0]).join(' · ')}
        </p>
      )}
      {/* docs/93 AQ10: how the hero moves when it is not plainly on foot, and the water's hold on it */}
      {field.moves?.map((m) => <p key={m} className="fk-hud__footing fk-hud__moves" data-testid="hud-moves"><b>{field.hero ?? 'Hero'}</b> {m}</p>)}
    </section>
  );
}

const SKY_GLYPH: Record<string, string> = {
  Overcast: '☁', Drizzle: '☂', Rain: '☂', Thunderstorm: 'ϟ', Mist: '≋', Fog: '≋', Flurries: '❄', Snow: '❄',
  'Heat haze': '☀', Scorching: '☀', Dust: '≈', Sandstorm: '≈', Blizzard: '❄', Frost: '✱', 'Hard frost': '✱', Sleet: '☂',
};

/** `projected.ended` (fk_sim EncounterOutcome) → the callout's stamp. */
const ENDINGS: Record<string, { tone: StampTone; word: string }> = {
  Victory: { tone: 'victory', word: 'Victory' }, Surrendered: { tone: 'neutral', word: 'They yield' },
  Wipe: { tone: 'defeat', word: 'The party falls' }, Fled: { tone: 'neutral', word: 'Escape' }, Stalemate: { tone: 'neutral', word: 'Stalemate' },
  Failed: { tone: 'defeat', word: 'All is lost' },
};

/**
 * TARGET PREVIEW (UI specifics 06): hovering a unit that one of the plans hits shows the exact
 * outcome from the projection — the HP change, the figure, then the knock-on in a plain sentence
 * (what the foe still does after you, or that it drops first). Nothing here is computed.
 */
function TargetPreview() {
  const cursor = useAuthoring((s) => s.cursor);
  const projected = useAuthoring((s) => s.projected);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const me = useAuthoring((s) => s.me);
  const snapshot = useEncounter((s) => s.snapshot);
  const owners = useEncounter((s) => s.owners);
  const zoom = useView((s) => s.zoom);
  const scroll = useView((s) => s.scroll);
  const viewport = useView((s) => s.viewport);
  const field = useView((s) => s.field);
  if (!projected || !snapshot || !viewport) return null;
  const unit = snapshot.units.find((u) => u.onBoard !== false && covers(u, cursor));
  if (!unit) return null;
  const pu = projected.units.find((u) => u.unitId === unit.unitId);
  type Hit = { heroId: number; slot: string; action?: number; kind: 'hit' | 'heal'; amount: number; from?: Tile };
  const hits: Hit[] = [];
  for (const e of projected.effects) {
    const action = (local.get(`${e.id.heroId}:${e.id.slot}`) ?? rows.get(`${e.id.heroId}:${e.id.slot}`))?.action;
    if (e.kind === 'attack' && e.target === unit.unitId) hits.push({ heroId: e.id.heroId, slot: String(e.id.slot), action, kind: 'hit', amount: e.damage, from: e.from });
    if (e.kind === 'area') for (const h of e.hits) if (h.target === unit.unitId) hits.push({ heroId: e.id.heroId, slot: String(e.id.slot), action, kind: 'hit', amount: h.damage });
    if (e.kind === 'support' && e.beneficiaries.includes(unit.unitId) && e.amount !== undefined) hits.push({ heroId: e.id.heroId, slot: String(e.id.slot), action, kind: 'heal', amount: e.amount });
  }
  if (!hits.length || !pu) return null;
  hits.sort((a, b) => Number(owners[b.heroId] === me) - Number(owners[a.heroId] === me));
  const h = hits[0]!;
  const striker = snapshot.units.find((u) => u.heroId === h.heroId);
  const def = h.action !== undefined ? actionFor(h.action, striker) : undefined;
  const heroName = striker?.name ?? 'hero';
  const far = def ? strengthOf(def, striker?.arms).range : undefined;
  const decl = projected.declarations.find((d) => d.actor === unit.unitId);
  const foe = unit.faction === 'enemy';
  const after = !foe ? undefined : pu.revived ? 'It drops, then gets back up staggered.' : pu.dead ? 'It drops before it can act.' : decl && !decl.held
    ? `It still acts: ${intentLine(decl, snapshot.units)}.` : decl?.held ? 'It holds this turn.' : undefined;
  // docs/29: what the sky did to this number ("☀ −1 · Sun in the east"), and the wind on a bow's range
  const sways = (projected.swayed ?? []).filter((s) => s.unit === unit.unitId && s.heroId === h.heroId && String(s.slot) === h.slot);
  const wind = def && def.range > 1 && def.missile && h.from ? windFor(field?.windShift, h.from, unit.tile) : 0;
  const px = DEFAULT_CELL * zoom;
  const left = Math.round((unit.tile.x + 1) * px - scroll.x * zoom) + 6 - viewport.x;
  const top = Math.round(unit.tile.y * px - scroll.y * zoom) - viewport.y;
  const flip = left + 230 > viewport.w;
  return (
    <div className={`fk-hud__preview fk-hud__preview--${h.kind}`} role="status" data-testid="target-preview" style={{ left: flip ? left - px - 12 - 220 : left, top: Math.max(0, top) }}>
      <span className="fk-hud__preview-eyebrow">{heroName} · {def?.name ?? 'plan'}{hits.length > 1 ? ` · +${hits.length - 1} more` : ''}</span>
      <b className="fk-hud__preview-name">{unit.name}</b>
      <span className="fk-hud__preview-hp">HP {pu.hpStart} <span className="fk-hud__arrow">→</span> <b>{Math.max(0, pu.hpEnd)}</b> · {h.kind === 'heal' ? `+${h.amount}` : `${h.amount} damage`}{far ? `, ${far.melee ? 'reach' : 'range'} ${far.n}` : ''}{wind ? `, ${wind > 0 ? '+' : '−'}${Math.abs(wind)} with the wind` : ''}</span>
      {sways.map((s) => <span key={`${s.modifier}${s.delta}`} className="fk-hud__preview-sway" data-testid="preview-sway" data-modifier={s.modifier}>{swayLine(s, field?.modifiers)}</span>)}
      {after && <span className={`fk-hud__preview-after ${pu.dead ? 'fk-hud__preview-after--good' : ''}`}>{after}</span>}
    </div>
  );
}

const BARK_HOLD_MS = 5200;

/**
 * The leader's bark (11 §10.7): whenever the leader changes its order (docs/90: the sim's
 * `snapshot.orderCall`, made only on a change and with its own words), a portrait + "Name ·
 * leader's order" tag + the quoted line over the bottom-left of the board, gone after a few
 * seconds or on click. A standing order is never barked, so the room opens quiet. In the World's opening the face-off speaks the battle cry
 * (Opening.tsx), so the first pulse stays quiet; a named foe who gets away while the fight goes on
 * (the Beast's brood) barks their vow as they go (docs/22 O4).
 */
function LeaderBark() {
  const encounterId = useEncounter((s) => s.encounterId);
  // docs/56: the leader barks the new order once the pulse that brought it has played out
  const board = useShownSnapshot();
  const call = board?.orderCall;
  const pulse = board?.pulseIndex;
  const ended = useEncounter((s) => s.state === 3);
  const { name, unit } = useLeader();
  const story = useOpeningStory(encounterId);
  const escaped = useCinematic((s) => encounterId !== undefined && s.escaped === encounterId);
  // docs/77: any other named foe whose plot armour held barks a stock line as they go; a quest
  // leader who got away and rose as a named foe promises to meet the party again
  const fate = useFoeFate(encounterId);
  const risen = fate?.fate === FATE.RISEN;
  const vow = escaped && !ended ? (story ? story.battle.named_foe?.vow : fate?.fate === FATE.ESCAPED ? GETAWAY_LINE : undefined) : risen ? RISEN_LINE : undefined;
  const heroNames = useEncounter((s) => s.heroNames);
  // docs/41: a room with nobody to give orders (a puzzle room) has no bark; docs/83 PO2: nor does
  // a leader with no underlings standing on the board to hear them
  const underlings = !!board && hasUnderlings(board);
  const key = encounterId === undefined ? undefined : vow ? `${encounterId}:${risen ? 'risen' : 'escape'}` : call && underlings && !(story && (pulse ?? 1) <= 1) ? `${encounterId}:${call.pulse}:${call.order}` : undefined;
  const [shown, setShown] = useState<string>();
  const [leaving, setLeaving] = useState(false);
  useEffect(() => { if (key) { setShown(key); setLeaving(false); } }, [key]);
  useEffect(() => (leaving ? afterMotion(220, () => setShown(undefined)) : undefined), [leaving]);
  if (!shown || shown !== key || (!call && !vow)) return null;
  const text = vow ? withHeroes(vow, Object.values(heroNames)) : call!.line;
  // docs/91 OB10: a change of order is a moment — the camera pushes in on the leader as it shouts
  const caller = !vow && call ? board?.units.find((u) => u.unitId === call.leader) : undefined;
  return <BarkLine key={shown} name={story ? display(story.battle.leader.name) : fate && vow ? fate.name : name} frame={unit?.appearance} text={text} tag={vow ? (risen ? 'a named foe now' : 'gets away') : undefined} leaving={leaving} onLeave={() => setLeaving(true)} focusOn={caller && caller.onBoard !== false ? caller.tile : undefined} />;
}

/** How much closer than the player's own zoom a leader's call frames it, and how long it holds after the line is said. */
export const CALL_ZOOM = 2;
export const CALL_HOLD_MS = 1200;

/** docs/92 HM8: what each moment calls the huge foe's line on its tag. */
const HUGE_TAG: Record<HugeSay, string> = { reveal: 'appears', move: 'roars', bloodied: 'bloodied', triumph: 'exults', fall: 'falls' };

/**
 * docs/92 HM8: a huge foe speaks — on its reveal, its moves, when it is bloodied, when a hero
 * falls before it, and as it falls — in the leader's bark card, set above it (the leader's
 * order may be barked at the same time) with an ember tag.
 */
function HugeBark() {
  const bark = useHuge((s) => s.bark);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => { setLeaving(false); }, [bark?.key]);
  useEffect(() => (leaving && bark ? afterMotion(220, () => useHuge.getState().clear(bark.key)) : undefined), [leaving, bark]);
  if (!bark) return null;
  return <BarkLine key={bark.key} name={bark.name} frame={bark.appearance} text={bark.text} tag={HUGE_TAG[bark.say]} leaving={leaving} onLeave={() => setLeaving(true)} variant="huge" say={bark.say} />;
}

/** docs/83 PO2: a foe other than the leader still stands on the board (someone to take an order). */
export function hasUnderlings(board: Pick<Snapshot, 'units' | 'leaderUnit'>): boolean {
  const standing = board.units.filter((u) => u.faction === 'enemy' && u.onBoard !== false && !u.downed && u.hp > 0);
  // with no leader marked, whoever barks is one of them: it needs someone else beside it
  return board.leaderUnit === undefined ? standing.length > 1 : standing.some((u) => u.unitId !== board.leaderUnit);
}

function BarkLine({ name, frame, text, tag, leaving, onLeave, focusOn, variant, say }: { name: string; frame?: number; text: string; tag?: string; leaving: boolean; onLeave: () => void; focusOn?: Tile; variant?: 'huge'; say?: HugeSay }) {
  // docs/91: a leader that does not talk (a wolf, a spider, an ooze) acts: `*…*`, told, not quoted
  const act = /^\*.*\*$/.test(text.trim());
  const { shown, done, skip } = useTypewriter(act ? text.trim().slice(1, -1) : text);
  // Hold for a reading beat once the line is fully typed, then fade out and unmount.
  useEffect(() => { if (!done || leaving) return; const t = setTimeout(onLeave, BARK_HOLD_MS); return () => clearTimeout(t); }, [done, leaving, onLeave]);
  // docs/91 OB10: frame the leader while it shouts, then hand the camera back to the player where
  // they had it (the scene keeps their framing and eases back; reduced motion never moves it)
  const fx = focusOn?.x, fy = focusOn?.y;
  const [framing, setFraming] = useState(fx !== undefined);
  useEffect(() => { if (!done || leaving) return; const t = setTimeout(() => setFraming(false), CALL_HOLD_MS); return () => clearTimeout(t); }, [done, leaving]);
  useEffect(() => {
    if (fx === undefined || fy === undefined || !framing || leaving) return;
    const cine = useCinematic.getState();
    if (cine.faceoffPlaying || cine.focus) return; // the face-off (or another moment) has the camera
    cine.setFocus({ x: fx, y: fy }, CALL_ZOOM);
    return () => { const c = useCinematic.getState(); if (c.focus?.x === fx && c.focus?.y === fy) c.setFocus(undefined); };
  }, [fx, fy, framing, leaving]);
  return (
    <div className={`fk-hud__bark fk-hit ${variant ? `fk-hud__bark--${variant}` : ''} ${leaving ? 'fk-hud__bark--leaving' : ''}`} role="button" tabIndex={0} aria-label={`${name} says`} data-testid={variant ? `${variant}-bark` : 'leader-bark'} data-say={say} data-done={done}
      onClick={() => (done ? onLeave() : skip())} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (done) onLeave(); else skip(); } }}>
      <span className="fk-hud__bark-face"><Portrait frame={frame} scale={2} title={name} /></span>
      <span className="fk-hud__bark-body">
        <span className="fk-hud__bark-tag">{name} · {tag ?? <>leader&rsquo;s order</>}</span>
        {act ? <span className="fk-hud__bark-line fk-hud__bark-line--act" data-act="true">{shown}</span> : <q className="fk-hud__bark-line">{shown}</q>}
      </span>
    </div>
  );
}

/**
 * docs/83 PO1: the heroes' first word waits for the player's first turn — the board is up, nothing
 * is playing back, and the face-off (which mounts a beat after the story row lands) is over. The
 * quiet beat before it lets a face-off that is about to start claim the screen first.
 */
function FirstTurnVoice() {
  const encounterId = useEncounter((s) => s.encounterId);
  const ready = useEncounter((s) => !!s.boardSnapshot && s.state !== 3 && !s.busy);
  const playing = usePlayback((s) => s.playing);
  const faceoff = useCinematic((s) => s.faceoffPlaying);
  const turn = encounterId !== undefined && ready && !playing && !faceoff;
  useEffect(() => {
    if (!turn || encounterId === undefined) return;
    const t = setTimeout(() => startFirstTurn(encounterId), 700);
    return () => clearTimeout(t);
  }, [turn, encounterId]);
  return null;
}

/** docs/50: battle beats' sights over the board; the face-off draws its own while it plays. */
function BeatSights() {
  const faceoff = useCinematic((s) => s.faceoffPlaying);
  return faceoff ? null : <SightLayer fixed />;
}
